/**
 * Read-only rows for one platform's profile copy. `stacked` puts each label
 * above its value, for the narrow Home command column.
 */

import React from "react";
import { SocialPlatform, SocialProfile } from "@/types";
import type { Locale } from "@/hooks/useLocale";
import { PROFILE_COPY, fieldLabel, fieldsFor } from "../socialProfiles";

interface SocialProfileRowsProps {
  platform: SocialPlatform;
  profile: SocialProfile | undefined;
  locale?: Locale;
  stacked?: boolean;
}

export const SocialProfileRows: React.FC<SocialProfileRowsProps> = ({
  platform,
  profile,
  locale = "en",
  stacked = false,
}) => {
  const rows = fieldsFor(platform).filter((f) => profile?.[f]);

  if (rows.length === 0) {
    return <p className="text-sm text-gray-500">{PROFILE_COPY[locale].notSet}</p>;
  }

  return (
    <dl className="divide-y divide-gray-100 text-sm">
      {rows.map((field) => (
        <div
          key={field}
          className={stacked ? "py-2" : "py-2 grid grid-cols-[8rem_1fr] gap-3"}
        >
          <dt
            className={
              stacked
                ? "text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400"
                : "text-gray-500"
            }
          >
            {fieldLabel(platform, field, locale)}
          </dt>
          <dd
            className={`font-medium text-gray-900 whitespace-pre-line break-words ${
              stacked ? "mt-0.5" : ""
            }`}
          >
            {profile?.[field]}
          </dd>
        </div>
      ))}
    </dl>
  );
};
